import { ofetch } from 'ofetch';
import type { FetchError } from 'ofetch';
import type { ApiErrorBody } from '@scala-watch/shared';

const API_URL = import.meta.env.VITE_API_URL ?? '';

/** Resolve a same-origin `/api/...` path or return absolute URLs unchanged (for `<img src>`). */
export function resolveUiAssetUrl(pathOrUrl: string | null | undefined): string | null {
  if (pathOrUrl == null || pathOrUrl === '') return null;
  if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) return pathOrUrl;
  const base = API_URL.replace(/\/$/, '');
  if (!base) return pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${base}${path}`;
}

/**
 * `ofetch` instance that:
 *   - prefixes the API base URL
 *   - sends cookies (Better Auth uses cookie sessions)
 *   - automatically attaches `X-Account-Id` from the active-account store
 *   - throws a typed error containing the parsed JSON body
 *
 * The active-account header is read from a tiny module-level getter set by
 * the account store, avoiding a direct dependency cycle.
 */
let activeAccountIdGetter: () => string | null = () => null;

export function setActiveAccountIdGetter(fn: () => string | null): void {
  activeAccountIdGetter = fn;
}

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? 'API error');
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export const api = ofetch.create({
  baseURL: API_URL,
  credentials: 'include',
  onRequest: ({ request, options }) => {
    const headers = new Headers(options.headers);
    const accountId = activeAccountIdGetter();
    if (accountId) {
      headers.set('x-account-id', accountId);
    }
    const urlStr =
      typeof request === 'string'
        ? request
        : request instanceof Request
          ? request.url
          : String(request ?? '');
    const match = urlStr.match(/\/api\/public\/status\/([^/]+)\/([^/?#]+)/);
    if (match) {
      const accountSlug = match[1];
      const pageSlug = match[2];
      try {
        const token =
          localStorage.getItem(`sp_token_${accountSlug}_${pageSlug}`) ||
          sessionStorage.getItem(`sp_token_${accountSlug}_${pageSlug}`);
        if (token) {
          headers.set('x-status-page-token', token);
        }
      } catch {
        /* ignore storage access restrictions */
      }
    }
    options.headers = headers;
  },
  onResponseError: ({ response }) => {
    const body = (response._data as ApiErrorBody | undefined) ?? {
      error: 'INTERNAL',
      message: 'Network error',
    };
    throw new ApiError(response.status, body);
  },
});

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

export async function uploadAccountLogo(accountId: string, file: File): Promise<void> {
  const fd = new FormData();
  fd.append('logo', file);
  await api(`/api/accounts/${accountId}/logo`, { method: 'POST', body: fd });
}

export type { FetchError };
